import React, { useEffect, useState } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import Notification from '../Notification';
import '../../css/dashboard.css';
import apiService from '../../shared/services/ApiService';
import Cookies from 'js-cookie';

Chart.register(...registerables);

const Dashboard = () => {
    const [sectors, setSectors] = useState([]);
    const [circunscripcions, setCircunscripcions] = useState([]);
    const [filterCurrentMonth, setFilterCurrentMonth] = useState(true);
    const [brigadas, setBrigadas] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [activeView, setActiveView] = useState('bar'); // 'bar' o 'pie'
    const [statsLoading, setStatsLoading] = useState(true);
    const [filters, setFilters] = useState({
        circunscripcion: '',
        sector: '',
        brigada: '',
        estado: ''
    });
    const userRole = Cookies.get('role');
    const userId = Cookies.get('userId');
    const [summary, setSummary] = useState({
        pendiente: 0,
        proceso: 0,
        listo: 0,
        total: 0
    });
    const today = new Date().toISOString().split('T')[0];
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);

    // Colores consistentes para estados
    const statusColors = {
        'Pendiente': '#ef5ba1', // Rosa
        'Proceso': '#ffea00',   // Amarillo
        'Listo': '#00a651'      // Verde
    };

    useEffect(() => {
        const filteredNotifications = sectors.filter(sector => {
            const sectorDate = sector.scheduledDate?.split('T')[0];
            return sectorDate === today;
        }).map(sector => ({
            message: `Sector ${sector.name} programado para hoy`,
            status: sector.status
        }));
        setNotifications(filteredNotifications);
    }, [sectors, today]);
    // Paso 1: Cargar estadísticas rápidas (sin populate, agregación en servidor)
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const statsRes = await apiService.get('/api/sectores/stats?currentMonth=true');
                setSummary(statsRes.data || { total: 0, pendiente: 0, proceso: 0, listo: 0 });
            } catch (error) {
                console.error("Error al cargar stats:", error);
            } finally {
                setStatsLoading(false);
            }
        };
        fetchStats();
    }, []);

    // Paso 2: Cargar datos completos (sectores, circunscripciones, brigadas) en paralelo
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [sectorRes, circunsRes, brigadaRes] = await Promise.all([
                    apiService.get('/api/sectores'),
                    apiService.get('/api/circunscripciones'),
                    apiService.get('/api/brigadas')
                ]);

                let sectorsData = sectorRes.data || [];

                if (userRole !== "Administrador") {
                    sectorsData = sectorsData.filter(sector =>
                        sector.assignedBrigade?.members?.some(m => (m._id || m).toString() === userId)
                    );
                }

                setSectors(sectorsData);
                setFilteredData(sectorsData);
                setCircunscripcions(circunsRes.data || []);
                setBrigadas(brigadaRes.data || []);

                // Actualizar resumen con datos completos (reemplaza stats rápidos)
                updateSummary(sectorsData);
            } catch (error) {
                console.error("Error al cargar datos:", error);
                setNotifications(prev => [...prev, {
                    message: "Error al cargar datos. Intente de nuevo.",
                    status: "error"
                }]);
            }
        };

        fetchData();
    }, [userRole, userId]);  // eslint-disable-line react-hooks/exhaustive-deps

    const updateSummary = (data) => {
        const summary = data.reduce((acc, sector) => {
            acc.total++;
            if (sector.status === 'Pendiente') acc.pendiente++;
            else if (sector.status === 'Proceso') acc.proceso++;
            else if (sector.status === 'Listo') acc.listo++;
            return acc;
        }, { pendiente: 0, proceso: 0, listo: 0, total: 0 });

        setSummary(summary);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const newFilters = { ...filters, [name]: value };
        setFilters(newFilters);

        const filtered = sectors.filter(sector => {
            // Extraer el ID de la brigada (puede ser objeto o string)
            const brigadaId = typeof sector.assignedBrigade === 'object'
                ? sector.assignedBrigade?._id
                : sector.assignedBrigade;

            // Validar mes actual si el filtro está activo
            let isCurrentMonth = true;
            if (filterCurrentMonth && sector.scheduledDate) {
                const sectorDate = new Date(sector.scheduledDate);
                const now = new Date();
                isCurrentMonth = sectorDate.getMonth() === now.getMonth() &&
                    sectorDate.getFullYear() === now.getFullYear();
            }

            return (
                isCurrentMonth &&
                (newFilters.circunscripcion ? sector.circunscripcion === newFilters.circunscripcion : true) &&
                (newFilters.sector ? sector.name === newFilters.sector : true) &&
                (newFilters.brigada ? brigadaId === newFilters.brigada : true) &&
                (newFilters.estado ? sector.status === newFilters.estado : true)
            );
        });

        const result = filtered.length ? filtered : sectors;
        setFilteredData(result);
        updateSummary(result);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            circunscripcion: '',
            sector: '',
            brigada: '',
            estado: ''
        });
        setFilteredData(sectors);
        updateSummary(sectors);
        setCurrentPage(1);
         setFilterCurrentMonth(false);
    };

    const prepareBarData = () => {
        // Agrupar por estado para barras más significativas
        const statusGroups = filteredData.reduce((acc, sector) => {
            if (!acc[sector.status]) {
                acc[sector.status] = [];
            }
            acc[sector.status].push(sector);
            return acc;
        }, {});

        const labels = Object.keys(statusGroups);
        const data = labels.map(status => statusGroups[status].length);
        const backgroundColors = labels.map(status => statusColors[status]);

        return {
            labels,
            datasets: [
                {
                    label: 'Cantidad de Sectores',
                    data,
                    backgroundColor: backgroundColors,
                    borderColor: backgroundColors,
                    borderWidth: 1,
                    borderRadius: 8,
                    barThickness: 50,
                },
            ],
        };
    };

    const handleMonthFilterToggle = () => {
        const newValue = !filterCurrentMonth;
        setFilterCurrentMonth(newValue);

        const filtered = sectors.filter(sector => {
            const brigadaId = typeof sector.assignedBrigade === 'object'
                ? sector.assignedBrigade?._id
                : sector.assignedBrigade;

            // Validar mes actual si el filtro está activo
            let isCurrentMonth = true;
            if (newValue && sector.scheduledDate) {
                const sectorDate = new Date(sector.scheduledDate);
                const now = new Date();
                isCurrentMonth = sectorDate.getMonth() === now.getMonth() &&
                    sectorDate.getFullYear() === now.getFullYear();
            }

            return (
                isCurrentMonth &&
                (filters.circunscripcion ? sector.circunscripcion === filters.circunscripcion : true) &&
                (filters.sector ? sector.name === filters.sector : true) &&
                (filters.brigada ? brigadaId === filters.brigada : true) &&
                (filters.estado ? sector.status === filters.estado : true)
            );
        });

        const result = filtered.length ? filtered : sectors;
        setFilteredData(result);
        updateSummary(result);
        setCurrentPage(1);
    };

    const preparePieData = () => {
        return {
            labels: ['Pendiente', 'En Proceso', 'Listo'],
            datasets: [
                {
                    data: [summary.pendiente, summary.proceso, summary.listo],
                    backgroundColor: [statusColors['Pendiente'], statusColors['Proceso'], statusColors['Listo']],
                    borderColor: ['#fff', '#fff', '#fff'],
                    borderWidth: 2,
                },
            ],
        };
    };

    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',  // Barras horizontales para mejor visualización
        plugins: {
            title: {
                display: true,
                text: 'Sectores por Estado',
                font: {
                    size: 18,
                    weight: 'bold'
                }
            },
            tooltip: {
                callbacks: {
                    title: (tooltipItems) => {
                        return `Estado: ${tooltipItems[0].label}`;
                    },
                    label: (context) => {
                        return `Cantidad: ${context.raw} sectores`;
                    }
                }
            },
            legend: {
                display: false
            }
        },
        scales: {
            y: {
                grid: {
                    display: false
                }
            },
            x: {
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.05)'
                },
                ticks: {
                    precision: 0
                }
            }
        }
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true,
                text: 'Distribución de Sectores',
                font: {
                    size: 18,
                    weight: 'bold'
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.label;
                        const value = context.raw;
                        const percentage = ((value / summary.total) * 100).toFixed(1);
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            },
            legend: {
                position: 'bottom',
                labels: {
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            }
        }
    };


    return (
        <div className="dashboard-container p-6 bg-gray-50 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Panel de Control de Sectores</h2>

                <div className="flex space-x-2">
                    <button
                        className={`px-4 py-2 rounded-md ${activeView === 'bar' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                        onClick={() => setActiveView('bar')}
                    >
                        Barras
                    </button>
                    <button
                        className={`px-4 py-2 rounded-md ${activeView === 'pie' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                        onClick={() => setActiveView('pie')}
                    >
                        Pastel
                    </button>
                </div>
            </div>

            {/* Resumen de estadísticas */}
            <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
                    <h3 className="text-lg font-semibold text-gray-600">Total</h3>
                    <p className="text-3xl font-bold text-gray-800">{statsLoading ? '...' : summary.total}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4" style={{ borderColor: statusColors['Pendiente'] }}>
                    <h3 className="text-lg font-semibold text-gray-600">Pendientes</h3>
                    <p className="text-3xl font-bold text-gray-800">{statsLoading ? '...' : summary.pendiente}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4" style={{ borderColor: statusColors['Proceso'] }}>
                    <h3 className="text-lg font-semibold text-gray-600">En Proceso</h3>
                    <p className="text-3xl font-bold text-gray-800">{statsLoading ? '...' : summary.proceso}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4" style={{ borderColor: statusColors['Listo'] }}>
                    <h3 className="text-lg font-semibold text-gray-600">Completados</h3>
                    <p className="text-3xl font-bold text-gray-800">{statsLoading ? '...' : summary.listo}</p>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {/* Filtros en la barra lateral */}
                <div className="col-span-3 bg-white p-4 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">Filtros</h3>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Circunscripción</label>
                            <select
                                name="circunscripcion"
                                value={filters.circunscripcion}
                                onChange={handleFilterChange}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Todas</option>
                                {circunscripcions.map(circuns => (
                                    <option key={circuns._id} value={circuns._id}>{circuns.name}</option>
                                ))}
                            </select>
                        </div>


                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sector</label>
                            <select
                                name="sector"
                                value={filters.sector}
                                onChange={handleFilterChange}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Todos</option>
                                {sectors.map(sector => (
                                    <option key={sector._id} value={sector.name}>{sector.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Brigada (Solo visible para administradores) */}
                        {userRole === "Administrador" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Brigada</label>
                                <select
                                    name="brigada"
                                    value={filters.brigada}
                                    onChange={handleFilterChange}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Todas</option>
                                    {brigadas.map(brigada => (
                                        <option key={brigada._id} value={brigada._id}>
                                            {brigada.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                            <div className="flex space-x-2">
                                {Object.keys(statusColors).map(estado => (
                                    <button
                                        key={estado}
                                        onClick={() => handleFilterChange({ target: { name: 'estado', value: filters.estado === estado ? '' : estado } })}
                                        className={`flex-1 py-2 px-3 rounded-md text-sm ${filters.estado === estado ? 'text-white' : 'text-gray-700 bg-gray-100'}`}
                                        style={{ backgroundColor: filters.estado === estado ? statusColors[estado] : '' }}
                                    >
                                        {estado}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filterCurrentMonth}
                                    onChange={handleMonthFilterToggle}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                    Solo mes actual ({new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })})
                                </span>
                            </label>
                        </div>

                        <button
                            onClick={clearFilters}
                            className="w-full mt-4 bg-gray-200 text-gray-700 py-2 rounded-md hover:bg-gray-300"
                        >
                            Limpiar Filtros
                        </button>
                    </div>
                </div>

                {/* Gráficos principales */}
                <div className="col-span-9 bg-white p-4 rounded-lg shadow-sm">
                    <div className="h-96">
                        {activeView === 'bar' ? (
                            <Bar data={prepareBarData()} options={barOptions} />
                        ) : (
                            <Pie data={preparePieData()} options={pieOptions} />
                        )}
                    </div>

                    {/* Tabla de datos filtrados */}
                    {filteredData.length > 0 && (
                        <div className="mt-8">
                            <h3 className="text-lg font-semibold mb-3 text-gray-700">
                                Detalle de Sectores ({filteredData.length})
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="py-2 px-4 border-b text-left">Sector</th>
                                            <th className="py-2 px-4 border-b text-left">Circunscripción</th>
                                            <th className="py-2 px-4 border-b text-left">Brigada</th>
                                            <th className="py-2 px-4 border-b text-left">Estado</th>
                                            <th className="py-2 px-4 border-b text-left">Fecha Programada</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData
                                            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                            .map((sector) => {
                                                const brigada = brigadas.find(b => b._id === sector.assignedBrigade);
                                                const circun = circunscripcions.find(c => c._id === sector.circunscripcion);

                                                return (
                                                    <tr key={sector._id} className="hover:bg-gray-50">
                                                        <td className="py-2 px-4 border-b">{sector.name}</td>
                                                        <td className="py-2 px-4 border-b">{circun ? circun.name : '-'}</td>
                                                        <td className="py-2 px-4 border-b">{brigada ? brigada.name : '-'}</td>
                                                        <td className="py-2 px-4 border-b">
                                                            <span className="px-2 py-1 rounded-full text-xs text-white"
                                                                style={{ backgroundColor: statusColors[sector.status] }}>
                                                                {sector.status}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-4 border-b">
                                                            {sector.scheduledDate ? new Date(sector.scheduledDate).toLocaleDateString() : '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>

                                {/* Controles de paginación */}
                                {filteredData.length > itemsPerPage && (
                                    <div className="flex items-center justify-between mt-4 px-4">
                                        <div className="text-sm text-gray-600">
                                            Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredData.length)} de {filteredData.length} sectores
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Anterior
                                            </button>

                                            <div className="flex items-center space-x-1">
                                                {Array.from({ length: Math.ceil(filteredData.length / itemsPerPage) }, (_, i) => i + 1)
                                                    .filter(page => {
                                                        // Mostrar primera, última y páginas cercanas a la actual
                                                        const totalPages = Math.ceil(filteredData.length / itemsPerPage);
                                                        return page === 1 ||
                                                            page === totalPages ||
                                                            Math.abs(page - currentPage) <= 1;
                                                    })
                                                    .map((page, index, array) => (
                                                        <React.Fragment key={page}>
                                                            {index > 0 && array[index - 1] !== page - 1 && (
                                                                <span className="px-2 text-gray-500">...</span>
                                                            )}
                                                            <button
                                                                onClick={() => setCurrentPage(page)}
                                                                className={`px-3 py-1 rounded-md ${currentPage === page
                                                                    ? 'bg-blue-600 text-white'
                                                                    : 'border border-gray-300 hover:bg-gray-100'
                                                                    }`}
                                                            >
                                                                {page}
                                                            </button>
                                                        </React.Fragment>
                                                    ))}
                                            </div>

                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredData.length / itemsPerPage)))}
                                                disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
                                                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Siguiente
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Notificaciones */}
            <div className="fixed bottom-6 right-6 space-y-2 max-w-sm">
                {notifications.map((notification, index) => (
                    <Notification
                        key={index}
                        message={notification.message}
                        status={notification.status}
                        onClose={() => setNotifications(notifications.filter((_, i) => i !== index))}
                    />
                ))}
            </div>
        </div>
    );
};

export default Dashboard;